/**
 * CountryGreetings — plays country-specific hello / goodbye audio clips.
 *
 * Usage:
 *   const { playHello, playGoodbye } = useCountryGreeting('LA');
 *   playHello();   // plays sabaidee
 *   playGoodbye(); // plays la kon
 *
 * Audio files live in public/audio/greetings/<country_code>_hello.mp3 etc.
 */

import { useCallback, useRef } from 'react';

/** Registry of available greeting audio per country code (ISO-3166 alpha-2). */
const GREETING_AUDIO: Record<string, { hello?: string; goodbye?: string }> = {
  LA: {
    hello: '/audio/greetings/sabaidee_laos.mp3',
    goodbye: '/audio/greetings/la_kon_lao_goodbye.mp3',
  },
  BD: {
    hello: '/audio/greetings/assalamu_alaikum_bd.mp3',
    goodbye: '/audio/greetings/allah_hafez_bd.mp3',
  },
  PK: {
    hello: '/audio/greetings/assalamu_alaikum_pk.mp3',
    goodbye: '/audio/greetings/allah_hafez_pk.mp3',
  },
};

export function useCountryGreeting(countryCode?: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const play = useCallback(
    (type: 'hello' | 'goodbye') => {
      if (!countryCode) return;
      const entry = GREETING_AUDIO[countryCode.toUpperCase()];
      const src = entry?.[type];
      if (!src) return;

      // Stop any currently playing greeting
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      const audio = new Audio(src);
      audio.volume = 0.8;
      audioRef.current = audio;
      audio.play().catch((err) => {
        // Browsers may block autoplay — silently ignore
        console.warn('[CountryGreetings] playback blocked:', err.message);
      });
    },
    [countryCode],
  );

  const playHello = useCallback(() => play('hello'), [play]);
  const playGoodbye = useCallback(() => play('goodbye'), [play]);

  return { playHello, playGoodbye };
}
